import { restApi } from '../lib/rest-api';
import {
  CreateOrderRequest,
  CreateOrderResponse,
  UpdateOrderRequest,
  UpdateOrderResponse,
  GetOrderResponse,
} from '../models/rest/order';
import { ArticleGroupsRequest, ArticleGroupsResponse } from '../models/rest/article-groups';
import {
  RecommendedSizeChartParams,
  RecommendedSizeDistributionParams,
  RecommendedSizeChartsResponse,
  RecommendedDistribution,
} from '../models/rest/sizing';
import {
  BulkUpdateArticlesRequest,
  BulkUpdateArticlesResponse,
  UpdateArticlesReferenceBrandRequest,
} from '../models/rest/article';
import { ReferenceBrandsResponse, ReferenceBrandsParams, SilosResponse, SilosParams } from '../models/rest/reference';

/**
 * REST API Service for presize-backend
 * This class provides typed methods for REST endpoints.
 * It will coexist with the existing GraphQL PresizeBackend service during migration.
 */
export class PresizeBackendRest {
  // =============================================================================
  // Order Management
  // =============================================================================

  /**
   * Create a new order
   * Backend endpoint: POST /api/orders
   *
   * @param request Order creation request
   * @param signal Optional AbortSignal for request cancellation
   * @returns Created order metadata
   * @throws Error if order already exists (409) or validation fails (400)
   */
  async createOrder(request: CreateOrderRequest, signal?: AbortSignal): Promise<CreateOrderResponse> {
    return restApi<CreateOrderResponse>('POST', '/api/orders', request, undefined, signal, request.order_code);
  }

  /**
   * Get order by order code
   * Backend endpoint: GET /api/orders/{order_code}
   *
   * @param orderCode Order reference ID
   * @param signal Optional AbortSignal for request cancellation
   * @returns Order metadata
   * @throws Error if order not found (404)
   */
  async getOrder(orderCode: string, signal?: AbortSignal): Promise<GetOrderResponse> {
    return restApi<GetOrderResponse>('GET', `/api/orders/${orderCode}`, undefined, undefined, signal, orderCode);
  }

  /**
   * Update order settings (CG grouping level, enable box, status)
   * Backend endpoint: PATCH /api/orders/{order_code}
   *
   * @param orderCode Order reference ID
   * @param request Order update request with optional fields
   * @param signal Optional AbortSignal for request cancellation
   * @returns Updated order metadata
   * @throws Error if order not found (404) or validation fails (400)
   */
  async updateOrder(
    orderCode: string,
    request: UpdateOrderRequest,
    signal?: AbortSignal
  ): Promise<UpdateOrderResponse> {
    return restApi<UpdateOrderResponse>('PATCH', `/api/orders/${orderCode}`, request, undefined, signal, orderCode);
  }

  // =============================================================================
  // Article Groups
  // =============================================================================

  /**
   * Create/update article groups for an order
   * Backend endpoint: POST /api/orders/{order_code}/article-groups
   *
   * Groups are generated on-the-fly based on article attributes (CG code, size structure, etc.)
   * Groups are NOT persisted; they're regenerated each time this endpoint is called.
   *
   * @param orderCode Order reference ID
   * @param request Article groups request with list of articles
   * @param signal Optional AbortSignal for request cancellation
   * @returns Generated article groups
   */
  async createArticleGroups(
    orderCode: string,
    request: ArticleGroupsRequest,
    signal?: AbortSignal
  ): Promise<ArticleGroupsResponse> {
    return restApi<ArticleGroupsResponse>(
      'POST',
      `/api/orders/${orderCode}/article-groups`,
      request,
      undefined,
      signal,
      orderCode
    );
  }

  // =============================================================================
  // Size Charts & Distribution
  // =============================================================================

  /**
   * Get recommended size charts WITH distribution (first-time load, reference brand change)
   * Backend endpoint: GET /api/recommended-size-charts-with-distribution
   *
   * This endpoint returns size charts AND includes distribution for the first size key.
   * Use this for first-time users or when reference brand changes.
   *
   * @param params Query parameters for size chart recommendation
   * @param signal Optional AbortSignal for request cancellation
   * @returns Size charts with distribution information
   */
  async getSizeChartsWithDistribution(
    params: RecommendedSizeChartParams,
    signal?: AbortSignal
  ): Promise<RecommendedSizeChartsResponse> {
    return restApi<RecommendedSizeChartsResponse>(
      'GET',
      '/api/recommended-size-charts-with-distribution',
      undefined,
      params,
      signal
    );
  }

  /**
   * Get recommended size charts WITHOUT distribution (returning user, parallel load)
   * Backend endpoint: GET /api/recommended-size-charts
   *
   * This endpoint returns only size charts and size keys WITHOUT distribution.
   * Use this for returning users who need to load size charts in parallel with distribution.
   *
   * @param params Query parameters for size chart recommendation
   * @param signal Optional AbortSignal for request cancellation
   * @returns Size charts without distribution information
   */
  async getSizeCharts(
    params: RecommendedSizeChartParams,
    signal?: AbortSignal
  ): Promise<RecommendedSizeChartsResponse> {
    return restApi<RecommendedSizeChartsResponse>('GET', '/api/recommended-size-charts', undefined, params, signal);
  }

  /**
   * Get recommended size distribution for specific size chart and size keys
   * Backend endpoint: GET /api/recommended-size-distribution
   *
   * Use this when:
   * - User changes size chart/key
   * - Returning user needs distribution for saved selections
   * - Preview/comparison modal
   *
   * @param params Query parameters including size chart and size keys
   * @param signal Optional AbortSignal for request cancellation
   * @returns Recommended distribution for the given size keys
   */
  async getSizeDistribution(
    params: RecommendedSizeDistributionParams,
    signal?: AbortSignal
  ): Promise<RecommendedDistribution> {
    return restApi<RecommendedDistribution>('GET', '/api/recommended-size-distribution', undefined, params, signal);
  }

  // =============================================================================
  // Articles
  // =============================================================================

  /**
   * Bulk update article attributes (silo, reference brand, reviewed status, etc.)
   * Backend endpoint: PATCH /api/orders/{order_code}/articles
   *
   * Updates multiple articles in a single request. Only provided fields are updated.
   *
   * Pass contentType 'application/merge-patch+json' when clearing a field with an
   * explicit null (e.g. removing the reference brand) so merge-patch semantics apply.
   *
   * @param orderCode Order reference ID
   * @param request Bulk update request with article changes
   * @param signal Optional AbortSignal for request cancellation
   * @param contentType Optional Content-Type override (default application/json)
   * @returns Update results for each article
   * @throws Error if order not found (404) or validation fails (400)
   */
  async updateArticles(
    orderCode: string,
    request: BulkUpdateArticlesRequest | UpdateArticlesReferenceBrandRequest,
    signal?: AbortSignal,
    contentType?: string
  ): Promise<BulkUpdateArticlesResponse> {
    return restApi<BulkUpdateArticlesResponse>(
      'PATCH',
      `/api/orders/${orderCode}/articles`,
      request,
      undefined,
      signal,
      orderCode,
      contentType
    );
  }

  // =============================================================================
  // Reference Data
  // =============================================================================

  /**
   * Get list of available reference brands
   * Backend endpoint: GET /api/reference-brands
   *
   * @param params Optional query parameters (season_code, brand_codes filter)
   * @param signal Optional AbortSignal for request cancellation
   * @returns List of reference brands
   */
  async getReferenceBrands(params?: ReferenceBrandsParams, signal?: AbortSignal): Promise<ReferenceBrandsResponse> {
    return restApi<ReferenceBrandsResponse>('GET', '/api/reference-brands', undefined, params as any, signal);
  }

  /**
   * Get list of available silos
   * Backend endpoint: GET /api/silos
   *
   * @param params Query parameters (brand_code required, cg_codes optional)
   * @param signal Optional AbortSignal for request cancellation
   * @returns List of silos
   */
  async getSilos(params: SilosParams, signal?: AbortSignal): Promise<SilosResponse> {
    return restApi<SilosResponse>('GET', '/api/silos', undefined, params, signal);
  }
}

export default new PresizeBackendRest();
